import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, ChevronDown, ChevronRight, Lightbulb, X } from 'lucide-react';

const STORAGE_PREFIX = 'enska.guide.dismissed.';

type GuideKey =
  | 'dashboard' | 'analytics' | 'agents' | 'conversations' | 'handoffs'
  | 'approvals' | 'alerts' | 'skills' | 'tools' | 'integrations'
  | 'knowledge' | 'guardrails' | 'settings' | 'voice' | 'channels'
  | 'workflows' | 'triggers' | 'eventConnectors' | 'teams' | 'hitl' | 'monitor'
  | 'api-keys' | 'llm-providers' | 'audit-log' | 'secrets' | 'tools-catalog';


interface PageGuideProps {
  pageKey: GuideKey;
}

export function PageGuide({ pageKey }: PageGuideProps) {
  const { t } = useTranslation();

  const storageKey = `${STORAGE_PREFIX}${pageKey}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(storageKey) === '1');
  const [expanded, setExpanded] = useState(() => localStorage.getItem(storageKey) !== '1');

  const dismiss = useCallback(() => {
    localStorage.setItem(storageKey, '1');
    setDismissed(true);
  }, [storageKey]);

  const reopen = useCallback(() => {
    localStorage.removeItem(storageKey);
    setDismissed(false);
    setExpanded(true);
  }, [storageKey]);

  // Safely resolve translation keys — fall back to empty strings if key missing
  const title = t(`guide.${pageKey}.title`, '');
  const body = t(`guide.${pageKey}.body`, '');
  const tips: string[] = t(`guide.${pageKey}.tips`, { returnObjects: true }) as string[] ?? [];

  if (!body) return null;

  if (dismissed) {
    return (
      <button
        onClick={reopen}
        aria-label={t('guide.show')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '4px 10px', borderRadius: 6,
          border: '1px solid var(--border)',
          background: 'transparent', cursor: 'pointer',
          fontSize: 11, color: 'var(--color-muted)',
          marginBottom: 16,
          transition: 'color 0.15s, border-color 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.color = '#818cf8'; e.currentTarget.style.borderColor = '#6366f140'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'var(--color-muted)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
      >
        <HelpCircle size={11} /> {t('guide.show')}
      </button>
    );
  }

  return (
    <div style={{
      marginBottom: 24,
      borderRadius: 12,
      border: '1px solid #6366f128',
      background: 'linear-gradient(135deg, #6366f108 0%, #818cf805 100%)',
      overflow: 'hidden',
    }}>
      {/* Header row */}
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%', padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#a5b4fc', textAlign: 'left',
        }}
      >
        <HelpCircle size={14} style={{ flexShrink: 0 }} />
        <span style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>{title}</span>
        {expanded
          ? <ChevronDown size={13} style={{ opacity: 0.6 }} />
          : <ChevronRight size={13} style={{ opacity: 0.6 }} />
        }
        <span
          role="button"
          tabIndex={0}
          onClick={e => { e.stopPropagation(); dismiss(); }}
          onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); dismiss(); } }}
          style={{ marginLeft: 4, cursor: 'pointer', opacity: 0.5, display: 'flex', alignItems: 'center' }}
          aria-label={t('guide.dismiss')}
        >
          <X size={12} />
        </span>
      </button>

      {expanded && (
        <div style={{ padding: '0 16px 14px' }}>
          {/* Body */}
          <p style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.65, margin: '0 0 12px' }}>
            {body}
          </p>

          {/* Tips */}
          {tips.length > 0 && (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
              {tips.map((tip, i) => (
                <li key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 6,
                  fontSize: 12, color: '#94a3b8',
                }}>
                  <Lightbulb size={11} style={{ flexShrink: 0, marginTop: 2, color: '#f59e0b' }} />
                  {tip}
                </li>
              ))}
            </ul>
          )}

          {/* Dismiss button */}
          <button
            onClick={dismiss}
            style={{
              marginTop: 14,
              padding: '4px 12px', borderRadius: 6,
              border: '1px solid #6366f130',
              background: '#6366f110', color: '#818cf8',
              cursor: 'pointer', fontSize: 11, fontWeight: 600,
            }}
          >
            {t('guide.dismiss')} ✓
          </button>
        </div>
      )}
    </div>
  );
}
